import { useEffect, useRef, Component } from "react";
import "@/App.css";
import "lenis/dist/lenis.css";
import { BrowserRouter, Routes, Route, useLocation, Link } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";
import { CartProvider } from "@/context/CartContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Home from "@/pages/Home";
import Shop from "@/pages/Shop";
import Product from "@/pages/Product";
import CartPage from "@/pages/Cart";
import Checkout from "@/pages/Checkout";
import Confirmation from "@/pages/Confirmation";
import About from "@/pages/About";
import FAQ from "@/pages/FAQ";
import Contact from "@/pages/Contact";
import Accessories from "@/pages/Accessories";
import Admin from "@/pages/Admin";

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  render() {
    if (this.state.error) {
      return (
        <div className="min-h-screen flex flex-col items-start justify-center px-8 bg-white text-ink">
          <p className="eyebrow mb-4">Something stalled</p>
          <p className="font-display text-2xl font-bold">This section hit a snag.</p>
          <Link to="/" className="mt-6 rounded-[3px] bg-volt px-6 py-3 font-bold text-white" data-testid="error-back-home">
            Back to Home
          </Link>
        </div>
      );
    }
    return this.props.children;
  }
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    if (window.__lenis) {
      window.__lenis.scrollTo(0, { immediate: true });
    } else {
      window.scrollTo(0, 0);
    }
  }, [pathname]);
  return null;
}

function Shell() {
  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/shop" element={<Shop />} />
          <Route path="/accessories" element={<Accessories />} />
          <Route path="/product/:slug" element={<Product />} />
          <Route path="/cart" element={<CartPage />} />
          <Route path="/checkout" element={<Checkout />} />
          <Route path="/order-confirmation/:orderId" element={<Confirmation />} />
          <Route path="/about" element={<About />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>
      <Footer />
    </>
  );
}

function NotFound() {
  return (
    <div className="min-h-[70vh] flex flex-col items-start justify-center px-6 sm:px-10 max-w-3xl mx-auto w-full">
      <p className="eyebrow mb-4">404</p>
      <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight">
        Off the <span className="text-volt">grid</span>.
      </h1>
      <p className="mt-4 text-zinc-600">That page doesn't exist. Let's get you back on the road.</p>
      <Link to="/" data-testid="notfound-home-link" className="mt-8 rounded-[3px] bg-volt px-7 py-3.5 font-bold text-white hover:bg-volt-dim transition-colors duration-200">
        Back to Home
      </Link>
    </div>
  );
}

export default function App() {
  const lenisRef = useRef(null);

  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    lenisRef.current = lenis;
    window.__lenis = lenis;
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  return (
    <CartProvider>
      <BrowserRouter>
        <ErrorBoundary>
          <Routes>
            <Route path="/admin" element={<Admin />} />
            <Route path="*" element={<Shell />} />
          </Routes>
        </ErrorBoundary>
      </BrowserRouter>
      <Toaster
        position="bottom-right"
        toastOptions={{
          style: {
            background: "#FFFFFF",
            border: "1px solid rgba(0,0,0,0.1)",
            color: "#0A0A0A",
          },
        }}
      />
    </CartProvider>
  );
}
